import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Edit, Calendar, MapPin, Route, Gauge, Clock, Users, ChevronUp, Plus,
  Star, Download, Loader2, ThumbsUp, Pin, Sparkles, CalendarClock,
  Navigation, Globe, Mountain, Map as MapIcon, Wallet, Info, ZoomIn, Images,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Footer } from "@/components/Footer";
import { ShareModal } from "@/components/ShareModal";
import { TripCostsTab } from "@/components/trip/TripCostsTab";
import { ExportDialog } from "@/components/trip/ExportDialog";
import { PublicationStatusBadge, PlanningStatusBadge, DataBadge } from "@/components/TripBadges";
import { useAuth } from "@/contexts/AuthContext";
import { getTranslatedValue } from "@/components/TranslatableField";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PlanBadgeIcon } from "@/components/PlanBadgeAvatar";
import { getUploadUrl, getCloudinaryThumbnail } from "@/lib/utils";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { useTripDetail } from "./Trip/useTripDetail";
import { SEO } from "@/components/SEO";
import { SegmentCard } from "./Trip/SegmentCard";
import { TripSidebar } from "./Trip/TripSidebar";
import { TripLightboxes } from "./Trip/TripLightboxes";
import { TripOverviewMap } from "@/components/TripOverviewMap";
import { BACKEND_URL } from "@/config";
import { LenisProvider } from "@/components/trip/LenisProvider";
import { FloatingNavbar } from "@/components/trip/FloatingNavbar";
import { FadeInDirection } from "@/components/trip/FadeInDirection";

export default function TripDetail() {
  const d = useTripDetail();
  const {
    tripId, navigate, t, currentLang, authUser, isAuthenticated,
    setActionBarContent, clearActionBarContent,
    trip, loading, isOwner, allPhotos,
    isPinned, isLiked, likeCount,
    ownExportEnabled, othersExportEnabled, tripExportEnabled,
    togglePinMutation, toggleLikeMutation,
    user, exporting,
    highlightedPoint, setHighlightedPoint,
    exportDialogOpen, setExportDialogOpen,
    handlePinToggle, handleLikeToggle,
    handleProtectedDownload, handleExport,
    openLightbox, openAlojamentoLightbox, openFoodLightbox,
    formatDate, formatDuration,
  } = d;

  // Action bar setup
  useEffect(() => {
    if (!user || !trip) { clearActionBarContent(); return; }
    const tripIsOwner = trip.user_id === user.user_id;
    setActionBarContent(
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={() => navigate("/passeio/novo")} className="action-bar-btn" data-testid="new-trip-btn">
                <Plus className="h-4 w-4 sm:mr-1" /><span className="btn-label">{t("trip_detail.new_trip")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>{t("trip_detail.new_trip_tooltip")}</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {tripExportEnabled && isAuthenticated && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" disabled={exporting} onClick={() => setExportDialogOpen(true)} className="action-bar-btn" data-testid="export-trip-btn">
                  {exporting ? <Loader2 className="h-4 w-4 sm:mr-1 animate-spin" /> : <Download className="h-4 w-4 sm:mr-1" />}
                  <span className="btn-label">{exporting ? t("trip_detail.exporting") : t("trip_detail.export")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>{t("trip_detail.export_tooltip")}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {(tripIsOwner || trip.can_edit) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" onClick={() => navigate(`/passeio/${tripId}/editar`)} className="action-bar-btn" data-testid="edit-trip-btn">
                  <Edit className="h-4 w-4 sm:mr-1" /><span className="btn-label">{t("trip_detail.edit")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>{t("trip_detail.edit_tooltip")}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </>
    );
    return () => clearActionBarContent();
  }, [user, trip, ownExportEnabled, othersExportEnabled, exporting, isAuthenticated, t, navigate, tripId, setActionBarContent, clearActionBarContent]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="spinner" /></div>;
  if (!trip) return (
    // Soft-404 hardening: trip not found/private/removed. SPA can't return
    // HTTP 404, so emit noindex so Google drops the URL instead of Soft 404 (2026-07).
    <div className="min-h-screen hero-background pt-36 flex flex-col" data-testid="trip-not-found">
      <SEO title={t("trip_detail.not_found_title", "Passeio não disponível")} noindex />
      <AppHeader />
      <main className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <MapIcon className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-2xl font-bold">{t("trip_detail.not_found_title", "Passeio não disponível")}</h1>
        <p className="text-muted-foreground max-w-md">{t("trip_detail.not_found_desc", "Este passeio não existe ou já não é público.")}</p>
        <Button onClick={() => navigate("/passeios")} variant="outline" data-testid="trip-not-found-explore">
          {t("trip_detail.back_to_trips", "Explorar passeios")}
        </Button>
      </main>
      <Footer />
    </div>
  );

  // b99.9.100 — navItems computed at top-level so the FloatingNavbar can be
  // rendered inline with the title block (same y as the overline / underline),
  // sharing the same sticky behaviour as before.
  const hasOverviewMap = trip.segments?.length >= 1 && trip.segments.some(s => s.gpx_file || s.google_maps_url || (s.localities || []).some(l => l.lat && l.lng));
  const hasExportableTrack = trip.segments?.some(s => s.gpx_file || (s.route_geometry?.coords?.length > 0));
  const hasCosts = (trip.costs?.length ?? 0) > 0;
  const segCount = trip.segments?.length || 0;
  const navItems = [
    hasOverviewMap ? { id: "mapa", label: t("trip_detail.nav_map", "Mapa"), icon: MapIcon } : null,
    ...(trip.segments || []).map((seg, i) => ({
      id: `etapa-${i + 1}`,
      label: segCount === 1 ? t("trip_form.segments.segment", "Etapa") : `${t("trip_form.segments.segment", "Etapa")} ${i + 1}`,
      fullTitle: getTranslatedValue(seg.title, currentLang) || `${t("trip_form.segments.segment", "Etapa")} ${i + 1}`,
      icon: Route,
    })),
    hasCosts ? { id: "custos", label: t("admin.tabs.costs"), icon: Wallet } : null,
  ].filter(Boolean);

  return (
    <LenisProvider>
    <div className="min-h-screen hero-background pt-36 flex flex-col" data-testid="trip-detail-page">
      <SEO
        title={`${getTranslatedValue(trip.title, currentLang)} — ${t("seo_dynamic.trip_title_suffix")}`}
        description={getTranslatedValue(trip.description, currentLang) || getTranslatedValue(trip.title, currentLang)}
        path={`/passeio/${trip.trip_id}${trip.slug ? `/${trip.slug}` : ""}`}
        image={trip.main_photo ? `https://www.mototrips.net/api/uploads/${trip.main_photo}` : undefined}
        type="article"
      />
      <AppHeader />
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 flex-1">
        {/* b99.9.100 — FloatingNavbar agora `fixed` (sempre na mesma posição,
            sem salto entre inicial e sticky). Posicionada via viewport. */}
        <FloatingNavbar items={navItems} testId="trip-floating-navbar" />

        {/* Trip Header */}
        <div className="mb-6" id="topo">
          <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="h-12 w-12 md:h-16 md:w-16 rounded-full border-4 border-primary/50 shadow-xl bg-card/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Route className="h-6 w-6 md:h-8 md:w-8 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="overline drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {(trip.group?.club_id || trip.club?.club_id || trip.club_id) ? t("trip_detail.overline_club") : t("trip_detail.overline_personal")}
                </p>
                <h1 className="text-lg sm:text-2xl font-black tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] mt-1" data-testid="trip-title">
                  {getTranslatedValue(trip.title, currentLang)}
                </h1>
                {trip.countries?.length > 0 && (
                  <p className="text-base text-white font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] mt-1" data-testid="trip-countries-header">
                    {trip.countries.map(c => getTranslatedValue(c, currentLang) || c).join(", ")}
                    {trip.start_date && (
                      <>
                        <span className="hidden md:inline text-sm tabular-nums"><span className="mx-3 opacity-60">|</span>{formatDate(trip.start_date)}{trip.end_date && trip.end_date !== trip.start_date && ` - ${formatDate(trip.end_date)}`}</span>
                        <span className="block md:hidden text-sm tabular-nums mt-3">{formatDate(trip.start_date)}{trip.end_date && trip.end_date !== trip.start_date && ` - ${formatDate(trip.end_date)}`}</span>
                      </>
                    )}
                  </p>
                )}
                {/* b99.9.100 — Action circles (Share / Pin / Like / Editor "i") movidos
                    para baixo do título no lado esquerdo, libertando todo o canto sup. dir. do hero. */}
                <TooltipProvider>
                  <div className="flex flex-row items-center gap-1.5 mt-3" data-testid="trip-action-circles">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div><ShareModal type="trip" data={{ id: tripId, title: getTranslatedValue(trip.title, currentLang) }} variant="ghost" size="icon" className="h-8 w-8 rounded-full text-white bg-black/40 hover:bg-black/60 hover:text-white border-0" iconOnly={true} noTooltip={true} /></div>
                  </TooltipTrigger>
                  <TooltipContent>{t("trip_detail.share_tooltip")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={handlePinToggle} disabled={togglePinMutation.isPending} className={`h-8 w-8 flex items-center justify-center rounded-full transition-all ${isPinned ? "text-primary bg-black/60 hover:bg-black/70" : "text-white bg-black/40 hover:text-primary hover:bg-black/60"} ${togglePinMutation.isPending ? "opacity-50 cursor-wait" : ""}`} data-testid="pin-trip-detail">
                      <Pin className={`h-4 w-4 ${isPinned ? "fill-primary" : ""}`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{isPinned ? t("trip_detail.unpin_tooltip") : t("trip_detail.pin_tooltip")}</TooltipContent>
                </Tooltip>
                {!isOwner && !trip.is_collaborator && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button onClick={d.handleLikeToggle} disabled={toggleLikeMutation.isPending} className={`h-8 w-8 flex items-center justify-center rounded-full transition-all ${isLiked ? "text-red-500 bg-black/60 hover:bg-black/70" : "text-white bg-black/40 hover:text-red-500 hover:bg-black/60"} ${toggleLikeMutation.isPending ? "opacity-50 cursor-wait" : ""}`} data-testid="like-trip-detail">
                        <ThumbsUp className={`h-4 w-4 ${isLiked ? "fill-red-500" : ""}`} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{isLiked ? t("trip_detail.unlike_tooltip") : t("trip_detail.like_tooltip")}{likeCount > 0 && ` (${likeCount})`}</TooltipContent>
                  </Tooltip>
                )}
                {(isOwner || trip.is_collaborator) && likeCount > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="h-8 px-2.5 flex items-center gap-1 rounded-full text-red-500/80 bg-black/40">
                        <ThumbsUp className="h-4 w-4" /><span className="text-xs font-medium">{likeCount}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>{likeCount} {likeCount === 1 ? t("trip_detail.like_singular") : t("trip_detail.like_plural")}</TooltipContent>
                  </Tooltip>
                )}
                {/* b99.9.100 — Editor-only "i" badge: popover aparece À DIREITA
                    do círculo (side="right") com os badges colapsados.
                    Apenas owner / participant / collaborator vêem o círculo.
                    b99.10.18 (2026-05-19) — ring vermelho forte quando o
                    passeio precisa de atenção do editor para ser publicado.
                    b99.10.19 — Corrigido: o ring só aparece para
                    `publication_status === "draft"` (precisa de atenção),
                    não para `archived` ou `closed` (estados intencionais).
                    Como o backend já força `data_ok=false ⇒ draft` em 3
                    sítios (`update_trip`, `update_segment`, post-segment-
                    update health check em mutations.py:1366), basta testar
                    o publication_status. */}
                {(isOwner || trip.user_role === "participant" || trip.user_role === "collaborator") && (
                  <Popover>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className={`h-8 w-8 flex items-center justify-center rounded-full text-white bg-black/40 hover:bg-black/60 transition-all ${
                              trip.publication_status === "draft"
                                ? "ring-2 ring-red-500 ring-offset-2 ring-offset-black/30 animate-pulse"
                                : ""
                            }`}
                            data-testid="editor-info-badge"
                            data-pub-status={trip.publication_status || "draft"}
                            aria-label={t("trip_detail.editor_info_tooltip", "Informação visível apenas a editores")}
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        {trip.publication_status === "draft"
                          ? t("trip_detail.editor_info_draft_tooltip", "Passeio em rascunho — clica para ver detalhes")
                          : t("trip_detail.editor_info_tooltip", "Informação visível apenas a editores")}
                      </TooltipContent>
                    </Tooltip>
                    <PopoverContent side="right" align="center" sideOffset={8} className="p-3 max-w-xs w-auto bg-neutral-900/95 backdrop-blur-md border-neutral-700/50 text-neutral-100">
                      <div className="flex flex-col gap-2">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400">
                          {t("trip_detail.editor_only_badges", "Visível apenas a editores")}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {isOwner && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold cursor-default shadow-sm bg-orange-500/20 text-orange-300 border border-orange-400/50">
                                <Star className="h-3.5 w-3.5" />{t("trip_card.role_badge.owner")}
                              </span>
                            </TooltipTrigger><TooltipContent><p>{t("trip_card.role_badge.owner_tooltip")}</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          {!isOwner && trip.user_role === "participant" && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold cursor-default shadow-sm bg-blue-500/20 text-blue-300 border border-blue-400/50">
                                <Users className="h-3.5 w-3.5" />{t("trip_card.role_badge.participant")}
                              </span>
                            </TooltipTrigger><TooltipContent><p>{t("trip_card.role_badge.participant_tooltip")}</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          {!isOwner && trip.user_role === "collaborator" && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold cursor-default shadow-sm bg-purple-500/20 text-purple-300 border border-purple-400/50">
                                <Edit className="h-3.5 w-3.5" />{t("trip_card.role_badge.collaborator")}
                              </span>
                            </TooltipTrigger><TooltipContent><p>{t("trip_card.role_badge.collaborator_tooltip")}</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          {isOwner && <PublicationStatusBadge status={trip.publication_status || "draft"} />}
                          {trip.planning_status && trip.planning_status !== "past_dates" && <PlanningStatusBadge planningStatus={trip.planning_status} />}
                          {isOwner && <DataBadge dataOk={trip.data_ok} />}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                  </div>
                </TooltipProvider>
              </div>
            </div>
          </div>
          {/* b99.9.100 (2026-05-19) — Linha de badges removida.
              Todos os badges (Criador/Rascunho/Dados/Participante/Colaborador/Planning)
              colapsados no Popover do círculo "i" no topo direito do hero (apenas para
              quem tem direitos de editor). Público não-editor não vê nada disto. */}
        </div>

        {/* Trip Info */}
        {(trip.places?.length > 0 || trip.group) && (
        <div className="mb-6 flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-6 text-foreground drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          {trip.places?.length > 0 && (
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 md:h-5 md:w-5" /><span className="text-xs md:text-sm">{trip.places.join(" \u2022 ")}</span></div>
          )}
          {trip.group && (
            trip.club.can_access ? (
              <div className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors" onClick={() => {
                if (!isAuthenticated) { handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.group") }); window.scrollTo(0, 0); return; }
                navigate(`/clube/${trip.club.club_id}`); window.scrollTo(0, 0);
              }}>
                <Users className="h-4 w-4 md:h-5 md:w-5" /><span className="text-xs md:text-sm">{getTranslatedValue(trip.club.name, currentLang)}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4 md:h-5 md:w-5" /><span className="text-xs md:text-sm">{getTranslatedValue(trip.club.name, currentLang)}</span></div>
            )
          )}
        </div>
        )}

        {/* b99.9.99 (2026-05-19) — Ficha de Passeio redesign: tabs removidas,
            conteúdo plano em duas colunas com FloatingNavbar (Aceternity)
            + Lenis smooth-scroll. Coluna esquerda concentra mapa total +
            cada etapa + custos como secções ancoráveis; coluna direita
            mantém foto principal, descrição, métricas e sidebar
            (participantes, motos, vídeos, galeria). Melhora SEO porque
            todo o conteúdo das etapas passa a estar renderizado no DOM
            inicial em vez de escondido atrás de tabs. */}
        {(() => {
          // b99.9.100 — hasOverviewMap movido para top-level. Mantemos comentário.
          // b99.9.75 — Motard incorporado no footer do card de Descrição
          // (sem datas, alinhado à direita).
          const hasOwner = !!(((trip.group?.club_id || trip.club?.club_id || trip.club_id) && trip.club) || trip.owner);
          const motardFooter = hasOwner ? (
            <div className={`flex flex-row justify-end ${trip.description ? "mt-3 pt-3 border-t border-white/15" : ""}`}>
              {(trip.group?.club_id || trip.club?.club_id || trip.club_id) && trip.club ? (
                <div className="flex items-center gap-2" data-testid="trip-detail-club-info">
                  <Avatar className="h-6 w-6 border border-primary">
                    <AvatarImage src={(() => { const url = trip.club.logo || trip.club.photo; if (!url) return null; const full = url.startsWith('http') ? url : getUploadUrl(url); return getCloudinaryThumbnail(full, 48, 48); })()} alt={getTranslatedValue(trip.club.name, currentLang)} />
                    <AvatarFallback className="text-[10px] bg-primary/20 text-white">
                      {(getTranslatedValue(trip.club.name, currentLang) || "C").charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {trip.club.can_access ? (
                    <span className="text-sm font-semibold text-white cursor-pointer hover:underline" onClick={() => { navigate(`/clube/${trip.club.club_id}`); window.scrollTo(0, 0); }}>{getTranslatedValue(trip.club.name, currentLang)}</span>
                  ) : <span className="text-sm font-semibold text-white">{getTranslatedValue(trip.club.name, currentLang)}</span>}
                </div>
              ) : trip.owner ? (
                <div className="flex items-center gap-2" data-testid="trip-detail-motard-info">
                  <Avatar className="h-6 w-6 border border-primary">
                    <AvatarImage src={(() => { const url = trip.owner.picture; if (!url) return null; const full = url.startsWith('http') ? url : getUploadUrl(url); return getCloudinaryThumbnail(full, 48, 48); })()} alt={trip.owner.nickname || trip.owner.name} />
                    <AvatarFallback className="text-[10px] bg-primary/20 text-white">
                      {(trip.owner.name || "?").charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {(trip.owner.corner_visibility || "public") === "public" ? (
                    <span className="text-sm font-semibold text-white cursor-pointer hover:underline" onClick={() => navigate(`/meu-canto?motard=${trip.owner.user_id}`)}>{trip.owner.nickname || trip.owner.name}</span>
                  ) : <span className="text-sm font-semibold text-white">{trip.owner.nickname || trip.owner.name}</span>}
                  {trip.owner.badge && <PlanBadgeIcon badge={trip.owner.badge} size="h-4 w-4" />}
                </div>
              ) : null}
            </div>
          ) : null;
          const descriptionCard = (trip.description || hasOwner) ? (
            <div className="rounded-xl bg-black/60 backdrop-blur-sm border border-white/10 px-4 py-3 flex flex-col">
              {trip.description && (
                <p className="text-xs md:text-sm text-white/90 italic drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">{getTranslatedValue(trip.description, currentLang)}</p>
              )}
              {motardFooter}
            </div>
          ) : null;

          // b99.9.74 — cada métrica passa a ter o seu próprio card (mini-card).
          const metricCardCls = "flex items-center gap-2 p-3 bg-card border border-border rounded-xl";
          const metricCards = [];
          if (trip.distance_km) {
            metricCards.push(
              <div key="dist" className={metricCardCls}>
                <Gauge className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.distance")}</p>
                  <p className="text-sm font-bold font-mono">{parseFloat(trip.distance_km.toFixed(1))} km</p>
                </div>
              </div>
            );
          }
          if (trip.duration_hours) {
            metricCards.push(
              <div key="dur" className={metricCardCls}>
                <Clock className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.duration")}</p>
                  <p className="text-sm font-bold font-mono">{formatDuration(trip.duration_hours)}</p>
                </div>
              </div>
            );
          }
          if (trip.segments?.length > 0) {
            const segTypes = [...new Set(trip.segments.filter((s) => s.segment_type).map((s) => s.segment_type))];
            const segDiffs = [...new Set(trip.segments.filter((s) => s.difficulty).map((s) => s.difficulty))];
            const types = {
              estrada: { label: t("trip_detail.segment_type_road") }, touring: { label: t("trip_detail.segment_type_touring") },
              offroad: { label: t("trip_detail.segment_type_offroad") }, aventura: { label: t("trip_detail.segment_type_adventure") },
              nacionais: { label: t("trip_detail.segment_type_nacionais") },
            };
            const difficulties = {
              green: { label: t("trip_detail.difficulty_easy_short"), order: 0 }, blue: { label: t("trip_detail.difficulty_moderate_short"), order: 1 },
              red: { label: t("trip_detail.difficulty_hard_short"), order: 2 }, black: { label: t("trip_detail.difficulty_expert_short"), order: 3 },
            };
            const sorted = segDiffs.sort((a, b) => (difficulties[a]?.order || 0) - (difficulties[b]?.order || 0));
            const maxOrd = sorted.length > 0 ? Math.max(...sorted.map((dd) => difficulties[dd]?.order || 0)) : 0;
            if (segTypes.length > 0) {
              // b99.9.77 — color-coding por tipo (paridade com SegmentCard.jsx)
              const typeStyle = {
                nacionais: { Icon: Navigation, cls: "text-blue-500" },
                touring: { Icon: Globe, cls: "text-orange-500" },
                offroad: { Icon: Mountain, cls: "text-red-500" },
                estrada: { Icon: Route, cls: "text-primary" },
                aventura: { Icon: Mountain, cls: "text-amber-500" },
              };
              const isSingle = segTypes.length === 1;
              const single = isSingle ? typeStyle[segTypes[0]] : null;
              const TypeIcon = single?.Icon || Route;
              const typeCls = single?.cls || "text-primary";
              metricCards.push(
                <div key="type" className={metricCardCls}>
                  <TypeIcon className={`h-5 w-5 shrink-0 ${typeCls}`} />
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.type")}</p>
                    <p className={`text-sm font-bold ${isSingle ? typeCls : ""}`}>{isSingle ? (types[segTypes[0]]?.label || segTypes[0]) : t("trip_detail.type_mixed", "Misto")}</p>
                  </div>
                </div>
              );
            }
            if (sorted.length > 0) {
              // b99.9.77 — color-coding por nível (paridade com SegmentCard.jsx)
              const orderToColor = {
                0: { bar: "bg-green-500", text: "text-green-500" },
                1: { bar: "bg-blue-500", text: "text-blue-500" },
                2: { bar: "bg-yellow-500", text: "text-yellow-500" },
                3: { bar: "bg-red-500", text: "text-red-500" },
              };
              const maxColor = orderToColor[maxOrd] || orderToColor[0];
              metricCards.push(
                <div key="diff" className={metricCardCls}>
                  <div className="flex gap-0.5 shrink-0">
                    {[0,1,2,3].map((i) => (
                      <div key={i} className={`w-1.5 h-5 rounded-sm ${i <= maxOrd ? (orderToColor[i]?.bar || "bg-primary") : "bg-muted-foreground/30"}`} />
                    ))}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.difficulty")}</p>
                    <p className={`text-sm font-bold ${maxColor.text}`}>{sorted.length === 1 ? (difficulties[sorted[0]]?.label || sorted[0]) : t("trip_detail.difficulty_mixed", "Mista")}</p>
                  </div>
                </div>
              );
            }
          }
          const statsCard = metricCards.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 md:gap-4" data-testid="trip-stats-card">
              {metricCards}
            </div>
          ) : null;
          // b99.10.21 — Foto principal: clicar na imagem ou no ícone
          // de zoom (topo-direita) abre o lightbox Swiper na posição
          // da foto principal do passeio (não na primeira do array).
          // Clicar no ícone de fotos (fundo-direita) vai à galeria.
          const mainPhotoIdxInAll = trip.main_photo
            ? Math.max(0, allPhotos.findIndex((p) => p.filename === trip.main_photo))
            : 0;
          const mainPhotoCard = trip.main_photo ? (
            <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black/40 group">
              <button
                type="button"
                onClick={() => d.openLightbox(mainPhotoIdxInAll)}
                className="block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="main-photo-open-lightbox"
                aria-label={t("trip_detail.zoom_photo", "Ver foto em grande")}
              >
                <img
                  src={getUploadUrl(trip.main_photo)}
                  alt={getTranslatedValue(trip.title, currentLang) || ""}
                  className="block w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </button>

              {allPhotos.length > 0 && (
                <>
                  {/* Zoom icon — top right, always visible */}
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => d.openLightbox(mainPhotoIdxInAll)}
                          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center shadow-lg backdrop-blur-sm transition-colors"
                          data-testid="main-photo-zoom-btn"
                          aria-label={t("trip_detail.zoom_photo", "Ampliar foto")}
                        >
                          <ZoomIn className="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>{t("trip_detail.zoom_photo", "Ampliar foto")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {/* Gallery icon — bottom right, always visible */}
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={`/passeio/${tripId}/galeria`}
                          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-black/55 hover:bg-black/75 text-white shadow-lg backdrop-blur-sm transition-colors"
                          data-testid="main-photo-gallery-btn"
                          aria-label={t("trip_detail.gallery_tooltip", "Abrir galeria completa")}
                        >
                          <Images className="h-5 w-5" />
                          <span className="text-xs font-semibold">{allPhotos.length}</span>
                        </a>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>{t("trip_detail.gallery_tooltip", "Abrir galeria completa")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          ) : null;

          // b99.9.100 — navItems já computado no top-level do componente; FloatingNavbar agora inline com o título.
          return (
            <>
              <div className="flex flex-col gap-6 md:gap-8 mt-6 lg:grid lg:grid-cols-10 lg:gap-6 lg:items-start">
                {/* ─── LEFT COLUMN — photo · description · metrics · sidebar ───
                    On mobile, `display: contents` makes this wrapper "vanish":
                    its children become direct flex items of the outer flex
                    container and can be ordered freely via order-* utilities,
                    interleaved with items from the right column. On `lg+`
                    the wrapper turns back into a `flex flex-col` column at
                    `col-span-3`, restoring the desktop layout. */}
                <div className="contents lg:flex lg:flex-col lg:gap-3 lg:col-span-4 min-w-0 lg:order-1">
                  {mainPhotoCard && (
                    <FadeInDirection direction="up" testId="trip-right-main-photo-wrap" className="order-1 lg:order-none">
                      {mainPhotoCard}
                    </FadeInDirection>
                  )}
                  {descriptionCard && (
                    <FadeInDirection direction="up" delay={0.08} testId="trip-right-description-wrap" className="order-2 lg:order-none">
                      {descriptionCard}
                    </FadeInDirection>
                  )}
                  {statsCard && (
                    <FadeInDirection direction="up" delay={0.16} testId="trip-right-stats-wrap" className="order-[91] lg:order-none">
                      {statsCard}
                    </FadeInDirection>
                  )}
                  <FadeInDirection direction="up" delay={0.24} testId="trip-right-sidebar-wrap" className="order-[92] lg:order-none">
                    <TripSidebar
                      trip={trip} allPhotos={allPhotos} currentLang={currentLang} t={t}
                      isAuthenticated={isAuthenticated} navigate={navigate} tripId={tripId}
                      isOwner={isOwner}
                    />
                  </FadeInDirection>
                </div>

                {/* ─── RIGHT COLUMN — anchor sections (map → segments → costs) ─── */}
                <div className="contents lg:flex lg:flex-col lg:gap-6 lg:col-span-6 min-w-0 lg:order-2">
                  {hasOverviewMap && (
                    <FadeInDirection direction="left" testId="trip-section-mapa-wrap" className="order-3 lg:order-none">
                      <section id="mapa" className="scroll-mt-24" data-testid="trip-section-mapa">
                        <TripOverviewMap
                          segments={trip.segments}
                          tripId={tripId}
                          tripName={getTranslatedValue(trip.title, currentLang)}
                          getSegmentTitle={(seg, i) => `${t("trip_form.segments.segment", "Etapa")} ${i + 1}`}
                          getSegmentFullTitle={(seg, i) => getTranslatedValue(seg.title, currentLang) || `${t("trip_form.segments.segment", "Etapa")} ${i + 1}`}
                          inlineHeightPx={500}
                        />
                        {tripExportEnabled && hasExportableTrack && (
                          <div
                            className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-border bg-card/80 backdrop-blur-sm px-4 py-3"
                            data-testid="trip-gpx-download-bar"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-foreground truncate">
                                {t("trip_detail.download_full_gpx", "Descarregar GPX do passeio completo")}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {t("trip_detail.download_full_gpx_hint", "Um ficheiro único, uma faixa por etapa.")}
                              </p>
                            </div>
                            <a
                              href={`${BACKEND_URL}/api/trips/${tripId}/download.gpx`}
                              onClick={(e) => handleProtectedDownload(`${BACKEND_URL}/api/trips/${tripId}/download.gpx`, `${getTranslatedValue(trip.title, currentLang) || "passeio"}.gpx`, e)}
                              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors"
                              data-testid="trip-gpx-download-btn"
                            >
                              <Download className="h-4 w-4" />
                              <span>{t("trip_detail.route_gpx", "GPX")}</span>
                            </a>
                          </div>
                        )}
                      </section>
                    </FadeInDirection>
                  )}

                  {trip.segments?.length > 0 ? (
                    trip.segments.map((segment, index) => {
                      // Etapa order on mobile = 4 + index (capped at 13 / 10
                      // etapas). Static class strings so Tailwind's JIT
                      // scanner picks them up at build time.
                      const ETAPA_ORDER_CLASSES = [
                        "order-[4]", "order-[5]", "order-[6]", "order-[7]", "order-[8]",
                        "order-[9]", "order-[10]", "order-[11]", "order-[12]", "order-[13]",
                      ];
                      const etapaOrder = ETAPA_ORDER_CLASSES[Math.min(index, ETAPA_ORDER_CLASSES.length - 1)];
                      return (
                        <FadeInDirection
                          key={segment.segment_id || index}
                          direction={index % 2 === 0 ? "right" : "left"}
                          // Stagger each segment by 80 ms so a multi-day
                          // trip reveals as a clear wave instead of all
                          // cards snapping in at once. Capped at 4 × 80 ms
                          // so trips with 10+ segments don't make the
                          // last card take 800 ms+ to appear.
                          delay={Math.min(index, 4) * 0.08}
                          testId={`trip-section-etapa-${index + 1}-wrap`}
                          className={`${etapaOrder} lg:order-none`}
                        >
                          <section id={`etapa-${index + 1}`} className="scroll-mt-24" data-testid={`trip-section-etapa-${index + 1}`}>
                            <SegmentCard
                              segment={segment} index={index} trip={trip}
                              currentLang={currentLang} t={t}
                              isAuthenticated={isAuthenticated} navigate={navigate} tripId={tripId}
                              formatDate={formatDate} formatDuration={formatDuration}
                              highlightedPoint={highlightedPoint} setHighlightedPoint={setHighlightedPoint}
                              handleProtectedDownload={handleProtectedDownload}
                              openAlojamentoLightbox={openAlojamentoLightbox} openFoodLightbox={openFoodLightbox}
                              tripExportEnabled={tripExportEnabled}
                            />
                          </section>
                        </FadeInDirection>
                      );
                    })
                  ) : (
                    <Card className="bg-card border-border rounded-xl">
                      <CardContent className="py-8">
                        <div className="text-center text-muted-foreground">
                          <Route className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t("trip_detail.no_segments")}</p>
                          {isOwner && <Button variant="link" className="text-primary" onClick={() => navigate(`/passeio/${tripId}/editar`)}>{t("trip_detail.add_segments")}</Button>}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {hasCosts && (
                    <FadeInDirection direction="right" delay={0.05} testId="trip-section-custos-wrap" className="order-[90] lg:order-none">
                      <section id="custos" className="scroll-mt-24" data-testid="trip-section-custos">
                        <TripCostsTab tripId={tripId} costs={trip.costs || []} canEdit={false} currency={authUser?.preferred_currency || "EUR"} />
                      </section>
                    </FadeInDirection>
                  )}
                </div>
              </div>
            </>
          );
        })()}

        <div className="flex justify-end mt-8">
          <Button variant="outline" size="sm" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="gap-2 bg-card/90 backdrop-blur-sm border-primary/50 hover:bg-primary hover:text-primary-foreground">
            <ChevronUp className="h-4 w-4" />{t("actions.top")}
          </Button>
        </div>
      </main>

      <TripLightboxes
        lightboxOpen={d.lightboxOpen} setLightboxOpen={d.setLightboxOpen}
        allPhotos={allPhotos} currentPhotoIndex={d.currentPhotoIndex} setCurrentPhotoIndex={d.setCurrentPhotoIndex}
        motoLightboxOpen={d.motoLightboxOpen} setMotoLightboxOpen={d.setMotoLightboxOpen}
        trip={trip} currentMotoPhotoIndex={d.currentMotoPhotoIndex} setCurrentMotoPhotoIndex={d.setCurrentMotoPhotoIndex} currentLang={currentLang}
        alojamentoLightboxOpen={d.alojamentoLightboxOpen} setAlojamentoLightboxOpen={d.setAlojamentoLightboxOpen}
        currentAlojamentoPhotos={d.currentAlojamentoPhotos} currentAlojamentoPhotoIndex={d.currentAlojamentoPhotoIndex} currentAlojamentoName={d.currentAlojamentoName}
        prevAlojamentoPhoto={d.prevAlojamentoPhoto} nextAlojamentoPhoto={d.nextAlojamentoPhoto}
        foodLightboxOpen={d.foodLightboxOpen} setFoodLightboxOpen={d.setFoodLightboxOpen}
        currentFoodPhotos={d.currentFoodPhotos} currentFoodPhotoIndex={d.currentFoodPhotoIndex} currentFoodName={d.currentFoodName}
        prevFoodPhoto={d.prevFoodPhoto} nextFoodPhoto={d.nextFoodPhoto}
      />

      <Footer />
      <ExportDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen} onExport={handleExport} canEdit={isOwner || trip?.can_edit} exporting={exporting} />
    </div>
    </LenisProvider>
  );
}

export { TripDetail };
